import { FeaturedCard, SiloSidebar } from '@/components/molecules';
import { Breadcrumb } from '@/components/atoms';
import { Comparator, EditorialContent } from '@/components/organisms';
import { BlockRenderer } from '@/components/BlockRenderer';
import { decodeHtmlEntities } from '@/utils/decodeHtmlEntities';
import { PLAN_TYPES } from '@/config/api';
import type { BreadcrumbItem } from '@/components/atoms/Breadcrumb/Breadcrumb';
import type { AppData } from '@/types/app';
import './SubComparator.scss';

const COMPARATOR_PARENTS: Record<string, BreadcrumbItem> = {
  [PLAN_TYPES.MOBILE]: { name: 'Forfait mobile', href: '/forfaits-mobiles-comparateur/' },
  [PLAN_TYPES.INTERNET]: { name: 'Forfait internet', href: '/comparateur/' },
};

interface SubComparatorProps {
  data: AppData;
}

export function SubComparator({ data }: SubComparatorProps) {
  const subComparator = data.subComparator;
  if (!subComparator) return null;

  const { comparatorType, featuredPlans, featuredPlanIds, highlightFavoriteIds } = subComparator;
  const parent = COMPARATOR_PARENTS[comparatorType];
  const breadcrumbItems = [
    ...(parent ? [parent] : []),
    ...(data.post?.breadcrumbs ?? []),
  ];
  const title = data.post?.title ?? '';
  const comparatorTags = subComparator.relatedTags ?? [];

  const coupDeCoeurIds = featuredPlans
    .filter((f) => f.coupDeCoeur)
    .map((f) => f.planId);
  const allHighlightIds = [
    ...(highlightFavoriteIds ?? data.highlightIds ?? []),
    ...coupDeCoeurIds,
  ];

  return (
    <div className="sub-comparator">
      {featuredPlans.length > 0 && (
        <section className="sub-comparator__featured">
          {featuredPlans.slice(0, 3).map((featured, index) => (
            <FeaturedCard
              key={featured.planId}
              planType={comparatorType}
              planId={featured.planId}
              showAnnonce={featured.showAnnonce}
              position={index + 1}
            />
          ))}
        </section>
      )}

      <div className="sub-comparator__header">
        <Breadcrumb items={breadcrumbItems} />
        <h1 className="sub-comparator__title">{decodeHtmlEntities(title)}</h1>
        {data.post?.resume && (
          <div
            className="sub-comparator__resume"
            dangerouslySetInnerHTML={{ __html: data.post.resume }}
          />
        )}
        {comparatorTags.length > 0 && (
          <nav className="sub-comparator__siblings" aria-label="Comparateurs liés">
            {comparatorTags.map((tag) => (
              <a
                key={tag.href}
                href={tag.href}
                className="sub-comparator__sibling-pill"
              >
                {tag.label}
              </a>
            ))}
          </nav>
        )}
      </div>

      <Comparator
        planType={comparatorType}
        highlightFavoriteIds={allHighlightIds}
        featuredPlanIds={featuredPlanIds ?? featuredPlans.map((featured) => featured.planId)}
      />

      {data.silo ? (
        <div className="with-silo-sidebar">
          <EditorialContent>
            <BlockRenderer blocks={data.blocks} />
          </EditorialContent>
          <SiloSidebar silo={data.silo} />
        </div>
      ) : (
        <EditorialContent>
          <BlockRenderer blocks={data.blocks} />
        </EditorialContent>
      )}
    </div>
  );
}
